import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import {
  APIuuid,
  IAPIArrayData,
  IAPIData
} from '@shared/interfaces/api.interface';
import { IFilter } from '@shared/interfaces/filter.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { IQuestionSetTemplate } from '@shared/interfaces/question-set-template.interface';
import { ISort } from '@shared/interfaces/sort.interface';
import { QuestionSetTemplate } from '@shared/models/question-set-template.model';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class QuestionSetsTemplatesService implements IPaginable {
  private readonly ENDPOINT_V3: string = String(
    `${environment.ENDPOINTS.BACK_END.V3}/question_set_templates`
  );

  constructor(private readonly _http: HttpClient) {}

  public delete(id: APIuuid): Observable<IQuestionSetTemplate> {
    return this._http
      .delete<IAPIData>(`${this.ENDPOINT_V3}/${id}`)
      .pipe(map((res: IAPIData) => QuestionSetTemplate.fromAPI(res)));
  }

  public find(id: APIuuid): Observable<IQuestionSetTemplate> {
    return this._http
      .get<IAPIData>(`${this.ENDPOINT_V3}/${id}`)
      .pipe(map((res: IAPIData) => QuestionSetTemplate.fromAPI(res)));
  }

  public get(filters?: any): Observable<IQuestionSetTemplate[]> {
    let params: HttpParams = new HttpParams();

    if (filters) {
      if (filters.page_size) {
        params = params.set(`page[page_size]`, filters.page_size);
      }

      if (filters.filter) {
        params = params.set(`filter[${filters.filter.key}]`, filters.filter.value);
      }
    }

    return this._http
      .get<IAPIArrayData>(this.ENDPOINT_V3, { params })
      .pipe(map((res: IAPIArrayData) => QuestionSetTemplate.fromAPIArray(res)));
  }

  public getPage(
    page: number,
    onGetRawData?: (data: IAPIArrayData) => void,
    filter?: IFilter,
    sort?: ISort,
    dates?: IFilter[]
  ): Observable<IQuestionSetTemplate[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('page[number]', String(page));

    if (!!filter && !!filter['key']) {
      params = params.set(`filter[${filter['key']}]`, filter['value']);
    }

    if (dates) {
      dates.map((f: IFilter) => {
        if (f['value']) {
          params = params.set(`dates[${f['key']}]`, f['value']);
        }
      });
    }

    if (sort) {
      const sortValue = sort['isReverse'] ? `-${sort['value']}` : sort['value'];
      params = params.set('sort', sortValue);
    }

    return this._http
      .get<IAPIArrayData>(this.ENDPOINT_V3, { params })
      .pipe(
        tap((data: IAPIArrayData) => onGetRawData && onGetRawData(data)),
        map((res: IAPIArrayData) => QuestionSetTemplate.fromAPIArray(res))
      );
  }

  public patch(body: IAPIData): Observable<IQuestionSetTemplate> {
    return this._http
      .patch<IAPIData>(`${this.ENDPOINT_V3}/${body.data.id}`, body)
      .pipe(map((res: IAPIData) => QuestionSetTemplate.fromAPI(res)));
  }

  public post(body: any): Observable<IQuestionSetTemplate> {
    return this._http
      .post<IAPIData>(this.ENDPOINT_V3, body)
      .pipe(map((res: IAPIData) => QuestionSetTemplate.fromAPI(res)));
  }

  public duplicate(id: APIuuid): Observable<IQuestionSetTemplate> {
    return this._http
      .post<IAPIData>(`${this.ENDPOINT_V3}/${id}/duplicate`, {})
      .pipe(map((res: IAPIData) => QuestionSetTemplate.fromAPI(res)));
  }
}
